import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import { PhotoIcon, VideoCameraIcon, XMarkIcon, PlusIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, Bars3Icon } from '@heroicons/react/24/outline';

interface Recipe {
  _id?: string;
  title: string;
  titleArabic?: string;
  description?: string;
  descriptionArabic?: string;
  cuisine: string;
  cuisineArabic?: string;
  sourceUrl?: string;
  categories?: string[];
  subcategories?: string[];
  ingredients: any[];
  steps: any[];
  timeMin: number;
  difficulty: string;
  equipment?: string[];
  equipmentIds?: string[];
  tags?: string[];
  tagsArabic?: string[];
  dietaryTags?: string[];
  dietaryTagsArabic?: string[];
  proTips?: string[];
  proTipsArabic?: string[];
  isTrending?: boolean;
  seasonalTags?: string[];
  cookingMethods?: any[];
  media?: {
    images?: string[];
    videos?: string[];
    arVideos?: string[];
    thumbnail?: string;
  };
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    fiber?: number;
    sugar?: number;
  };
  costEstimateEGP: number;
  mealBoxPriceEGP?: number;
  preparedMealPriceEGP?: number;
  servings: number;
  isPublished: boolean;
  buyBoxAllowed?: boolean;
  buyPreparedMealAllowed?: boolean;
  inventoryQuantityMealBox?: number;
  inventoryQuantityPreparedMeal?: number;
}

export default function RecipeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEditing = !!id;
  const isRTL = i18n.language === 'ar';
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const subcategoryDropdownRef = useRef<HTMLDivElement>(null);
  const equipmentDropdownRef = useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    recipeDetails: false,
    media: false,
    ingredients: false,
    steps: false,
    equipment: false,
    nutrition: false,
    tags: false,
    dietaryTags: false,
    seasonalTags: false,
    proTips: false,
    buyingOptions: false,
  });
  const tagInputRef = useRef<HTMLInputElement>(null);
  const dietaryTagInputRef = useRef<HTMLInputElement>(null);
  const seasonalTagInputRef = useRef<HTMLInputElement>(null);
  // Refs for buy-option checkboxes: always-rendered inputs so we can read .checked at submit
  const buyBoxAllowedInputRef = useRef<HTMLInputElement>(null);
  const buyPreparedMealAllowedInputRef = useRef<HTMLInputElement>(null);
  /** Drag-and-drop reorder (ingredients / steps): index of row being dragged, or null */
  const [draggingIngredientIndex, setDraggingIngredientIndex] = useState<number | null>(null);
  const [draggingStepIndex, setDraggingStepIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<Recipe>({
    title: '',
    titleArabic: '',
    description: '',
    descriptionArabic: '',
    cuisine: '',
    cuisineArabic: '',
    sourceUrl: '',
    categories: [],
    subcategories: [],
    ingredients: [{ name: '', nameArabic: '', amount: '', unit: '', unitArabic: '', notes: '', notesArabic: '', isHeader: false, storeLinks: [] }],
    steps: [{ stepNumber: 1, title: '', titleArabic: '', instruction: '', instructionArabic: '', timeMinutes: 0 }],
    timeMin: 30,
    difficulty: 'Beginner',
    equipment: [],
    equipmentIds: [],
    tags: [],
    tagsArabic: [],
    dietaryTags: [],
    dietaryTagsArabic: [],
    proTips: [],
    proTipsArabic: [],
    isTrending: false,
    seasonalTags: [],
    media: { images: [], videos: [], arVideos: [], thumbnail: '' },
    nutrition: { calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0, sugar: 0 },
    costEstimateEGP: 0,
    mealBoxPriceEGP: 0,
    preparedMealPriceEGP: 0,
    servings: 4,
    isPublished: false,
    buyBoxAllowed: false,
    buyPreparedMealAllowed: false,
    inventoryQuantityMealBox: 0,
    inventoryQuantityPreparedMeal: 0,
  });

  // Ref so submit always uses latest form state (avoids stale closure when checkbox is toggled right before save)
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    loadCategories();
    loadUnits();
    loadEquipment();
    if (isEditing) {
      loadRecipe();
    }
  }, [id]);

  const loadUnits = async () => {
    try {
      const response = await api.get('/units', { params: { activeOnly: true } });
      // Handle different response structures
      let unitsData = [];
      if (Array.isArray(response.data)) {
        unitsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        unitsData = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        unitsData = response.data.data;
      }
      setUnits(unitsData);
    } catch (error) {
      console.error('Error loading units:', error);
      setUnits([]);
    }
  };

  const loadEquipment = async () => {
    try {
      const response = await api.get('/admin/equipment', { params: { activeOnly: true } });
      let equipmentData = [];
      if (Array.isArray(response.data)) {
        equipmentData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        equipmentData = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        equipmentData = response.data.data;
      }
      setEquipment(equipmentData);
    } catch (error) {
      console.error('Error loading equipment:', error);
      setEquipment([]);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (subcategoryDropdownRef.current && !subcategoryDropdownRef.current.contains(event.target as Node)) {
        setShowSubcategoryDropdown(false);
      }
      if (equipmentDropdownRef.current && !equipmentDropdownRef.current.contains(event.target as Node)) {
        setShowEquipmentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCategories = async () => {
    try {
      const [catsRes, subsRes] = await Promise.all([
        api.get('/admin/categories', { params: { type: 'main', limit: 1000 } }),
        api.get('/admin/categories', { params: { type: 'sub', limit: 1000 } }),
      ]);
      
      // Handle different response structures
      let catsData = [];
      let subsData = [];
      
      if (catsRes.data?.success && catsRes.data?.data) {
        catsData = catsRes.data.data;
      } else if (Array.isArray(catsRes.data?.data)) {
        catsData = catsRes.data.data;
      } else if (Array.isArray(catsRes.data)) {
        catsData = catsRes.data;
      }
      
      if (subsRes.data?.success && subsRes.data?.data) {
        subsData = subsRes.data.data;
      } else if (Array.isArray(subsRes.data?.data)) {
        subsData = subsRes.data.data;
      } else if (Array.isArray(subsRes.data)) {
        subsData = subsRes.data;
      }
      
      console.log('Loaded categories:', catsData.length, 'subcategories:', subsData.length);
      console.log('Categories sample:', catsData.slice(0, 2));
      console.log('Subcategories sample:', subsData.slice(0, 2));
      
      setCategories(catsData);
      setSubcategories(subsData);
    } catch (error: any) {
      console.error('Error loading categories:', error);
      console.error('Error response:', error.response?.data);
      
      // Fallback: try to load from public endpoint
      try {
        const [catsRes] = await Promise.all([
          api.get('/categories', { params: { activeOnly: false } }),
        ]);
        
        const allCats = catsRes.data?.data || [];
        const catsData = allCats.filter((c: any) => c.type === 'main');
        const subsData = allCats.filter((c: any) => c.type === 'sub');
        
        setCategories(catsData);
        setSubcategories(subsData);
      } catch (fallbackError) {
        console.error('Error loading categories from fallback:', fallbackError);
      }
    }
  };

  const loadRecipe = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/recipes/${id}`);
      const recipe = response.data.data;
      setFormData({
        title: recipe.title || '',
        titleArabic: recipe.titleArabic || '',
        description: recipe.description || '',
        descriptionArabic: recipe.descriptionArabic || '',
        cuisine: recipe.cuisine || '',
        cuisineArabic: recipe.cuisineArabic || '',
        sourceUrl: recipe.sourceUrl || '',
        categories: recipe.categories?.map((c: any) => c._id || c) || [],
        subcategories: recipe.subcategories?.map((c: any) => c._id || c) || [],
        ingredients: recipe.ingredients?.length > 0 ? recipe.ingredients.map((ing: any) => ({
          name: ing.name || '',
          nameArabic: ing.nameArabic || '',
          amount: ing.amount || '',
          unit: ing.unit || '',
          unitArabic: ing.unitArabic || '',
          notes: ing.notes || '',
          notesArabic: ing.notesArabic || '',
          isHeader: ing.isHeader || false,
          storeLinks: ing.storeLinks || []
        })) : [{ name: '', nameArabic: '', amount: '', unit: '', unitArabic: '', notes: '', notesArabic: '', isHeader: false, storeLinks: [] }],
        steps:
          recipe.steps?.length > 0
            ? recipe.steps.map((s: any, i: number) => ({
                stepNumber: s.stepNumber ?? i + 1,
                title: s.title ?? '',
                titleArabic: s.titleArabic ?? '',
                instruction: s.instruction ?? '',
                instructionArabic: s.instructionArabic ?? '',
                timeMinutes: s.timeMinutes ?? 0,
                image: s.image,
                video: s.video,
                prepTime: s.prepTime,
                cookTime: s.cookTime,
              }))
            : [{ stepNumber: 1, title: '', titleArabic: '', instruction: '', instructionArabic: '', timeMinutes: 0 }],
        timeMin: recipe.timeMin || 30,
        difficulty: recipe.difficulty || 'Beginner',
        equipment: recipe.equipment || [],
        equipmentIds: recipe.equipmentIds || (Array.isArray(recipe.equipment) ? recipe.equipment : []) || [],
        tags: recipe.tags || [],
        tagsArabic: recipe.tagsArabic || [],
        dietaryTags: recipe.dietaryTags || [],
        dietaryTagsArabic: recipe.dietaryTagsArabic || [],
        proTips: recipe.proTips || [],
        proTipsArabic: recipe.proTipsArabic || [],
        isTrending: recipe.isTrending || false,
        seasonalTags: recipe.seasonalTags || [],
        cookingMethods: recipe.cookingMethods || [],
        media: recipe.media || { images: [], videos: [], arVideos: [], thumbnail: '' },
        nutrition: recipe.nutrition || { calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0, sugar: 0 },
        costEstimateEGP: recipe.costEstimateEGP || 0,
        mealBoxPriceEGP: recipe.mealBoxPriceEGP ?? 0,
        preparedMealPriceEGP: recipe.preparedMealPriceEGP ?? 0,
        servings: recipe.servings || 4,
        isPublished: recipe.isPublished || false,
        buyBoxAllowed: recipe.buyBoxAllowed ?? false,
        buyPreparedMealAllowed: recipe.buyPreparedMealAllowed ?? false,
        inventoryQuantityMealBox: recipe.inventoryQuantityMealBox ?? 0,
        inventoryQuantityPreparedMeal: recipe.inventoryQuantityPreparedMeal ?? 0,
      });
    } catch (error) {
      console.error('Error loading recipe:', error);
      alert(t('recipeForm.failedToLoad'));
      navigate('/?tab=recipes');
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (
    file: File,
    folder: string = 'recipes',
    type: 'image' | 'video' | 'document' | 'other' = 'image'
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    formData.append('type', type);
    
    setUploading(t('recipeForm.uploadingFile', { name: file.name }));
    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(null);
      if (response.data.success && response.data.data) {
        return response.data.data.url;
      }
      throw new Error('Upload failed');
    } catch (error: any) {
      setUploading(null);
      console.error('Upload error:', error);
      throw new Error(error.response?.data?.message || t('recipeForm.failedToUploadFile'));
    }
  };

  const uploadMultipleFiles = async (
    files: File[],
    folder: string = 'recipes',
    type: 'image' | 'video' | 'document' | 'other' = 'image'
  ): Promise<string[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('folder', folder);
    formData.append('type', type);
    
    setUploading(t('recipeForm.uploadingFiles', { count: files.length }));
    try {
      const response = await api.post('/upload/multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(null);
      if (response.data.success && response.data.data) {
        return response.data.data.map((item: any) => item.url);
      }
      throw new Error('Upload failed');
    } catch (error: any) {
      setUploading(null);
      console.error('Upload error:', error);
      throw new Error(error.response?.data?.message || t('recipeForm.failedToUploadFiles'));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'images' | 'thumbnail' | 'step') => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      if (type === 'thumbnail' && files.length === 1) {
        const url = await uploadFile(files[0], 'recipes/images', 'image');
        setFormData({
          ...formData,
          media: { ...formData.media, thumbnail: url },
        });
      } else if (type === 'images') {
        const urls = await uploadMultipleFiles(Array.from(files), 'recipes/images', 'image');
        setFormData({
          ...formData,
          media: { ...formData.media, images: [...(formData.media?.images || []), ...urls] },
        });
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      alert(error.message || t('recipeForm.failedToUploadImage'));
    }
    e.target.value = '';
  };

  const handleStepImageUpload = async (index: number, file: File) => {
    try {
      const url = await uploadFile(file, 'recipes/steps', 'image');
      const newSteps = [...formData.steps];
      newSteps[index] = { ...newSteps[index], image: url };
      setFormData({ ...formData, steps: newSteps });
    } catch (error: any) {
      console.error('Error uploading step image:', error);
      alert(error.message || t('recipeForm.failedToUploadStepImage'));
    }
  };

  const handleStepVideoUpload = async (index: number, file: File) => {
    try {
      const url = await uploadFile(file, 'recipes/steps', 'video');
      const newSteps = [...formData.steps];
      newSteps[index] = { ...newSteps[index], video: url };
      setFormData({ ...formData, steps: newSteps });
    } catch (error: any) {
      console.error('Error uploading step video:', error);
      alert(error.message || t('recipeForm.failedToUploadStepVideo'));
    }
  };

  const handleSubmit = async (e: React.FormEvent, publish: boolean = false) => {
    e.preventDefault();
    const latest = formDataRef.current;
    // Read from always-rendered checkbox inputs so value is never stale
    // Read directly from DOM so nothing can overwrite (omit from spread to be explicit)
    const buyBoxAllowed = buyBoxAllowedInputRef.current ? buyBoxAllowedInputRef.current.checked : Boolean(latest.buyBoxAllowed);
    const buyPreparedMealAllowed = buyPreparedMealAllowedInputRef.current ? buyPreparedMealAllowedInputRef.current.checked : Boolean(latest.buyPreparedMealAllowed);
    try {
      setLoading(true);
      const media = latest.media ? {
        ...latest.media,
        images: (latest.media.images || []).filter((url: string) => url?.trim() !== ''),
        videos: (latest.media.videos || []).filter((url: string) => url?.trim() !== ''),
        arVideos: (latest.media.arVideos || []).filter((url: string) => url?.trim() !== ''),
      } : latest.media;
      const { buyBoxAllowed: _b, buyPreparedMealAllowed: _p, ...rest } = latest;
      const submitData = {
        ...rest,
        media,
        isPublished: publish,
        ingredients: latest.ingredients.filter((ing: any) => ing.name.trim() !== ''),
        steps: latest.steps.filter((step: any) => step.instruction.trim() !== ''),
        buyBoxAllowed,
        buyPreparedMealAllowed,
      };
      
      if (isEditing) {
        await api.put(`/admin/recipes/${id}`, submitData);
      } else {
        await api.post('/admin/recipes', submitData);
      }
      navigate('/?tab=recipes');
    } catch (error: any) {
      console.error('Error saving recipe:', error);
      alert(error.response?.data?.message || t('recipeForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { name: '', nameArabic: '', amount: '', unit: '', unitArabic: '', notes: '', notesArabic: '', isHeader: false, storeLinks: [] }],
    });
  };

  const removeIngredient = (index: number) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_: any, i: number) => i !== index),
    });
  };

  const updateIngredient = (index: number, field: string, value: any) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const moveIngredient = (index: number, direction: 'up' | 'down') => {
    const newIngredients = [...formData.ingredients];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= newIngredients.length) return;
    [newIngredients[index], newIngredients[target]] = [newIngredients[target], newIngredients[index]];
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const reorderIngredient = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= formData.ingredients.length || toIndex >= formData.ingredients.length) return;
    const newIngredients = [...formData.ingredients];
    const [removed] = newIngredients.splice(fromIndex, 1);
    newIngredients.splice(toIndex, 0, removed);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newSteps = [...formData.steps];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= newSteps.length) return;
    [newSteps[index], newSteps[target]] = [newSteps[target], newSteps[index]];
    setFormData({ ...formData, steps: newSteps.map((s, i) => ({ ...s, stepNumber: i + 1 })) });
  };

  const reorderStep = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= formData.steps.length || toIndex >= formData.steps.length) return;
    const newSteps = [...formData.steps];
    const [removed] = newSteps.splice(fromIndex, 1);
    newSteps.splice(toIndex, 0, removed);
    setFormData({ ...formData, steps: newSteps.map((s, i) => ({ ...s, stepNumber: i + 1 })) });
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [
        ...formData.steps,
        {
          stepNumber: formData.steps.length + 1,
          title: '',
          titleArabic: '',
          instruction: '',
          instructionArabic: '',
          timeMinutes: 0,
        },
      ],
    });
  };

  const removeStep = (index: number) => {
    const newSteps = formData.steps.filter((_: any, i: number) => i !== index).map((step: any, i: number) => ({
      ...step,
      stepNumber: i + 1,
    }));
    setFormData({ ...formData, steps: newSteps });
  };

  const updateStep = (index: number, field: string, value: any) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  const addArrayItem = (field: keyof Recipe, value: string) => {
    const current = formData[field] as string[];
    setFormData({ ...formData, [field]: [...(current || []), value] });
  };

  const removeArrayItem = (field: keyof Recipe, index: number) => {
    const current = formData[field] as string[];
    setFormData({ ...formData, [field]: current.filter((_: any, i: number) => i !== index) });
  };


  const toggleCategory = (categoryId: string) => {
    const current = formData.categories || [];
    if (current.includes(categoryId)) {
      setFormData({ ...formData, categories: current.filter(id => id !== categoryId) });
    } else {
      setFormData({ ...formData, categories: [...current, categoryId] });
    }
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const toggleSubcategory = (subcategoryId: string) => {
    const current = formData.subcategories || [];
    if (current.includes(subcategoryId)) {
      setFormData({ ...formData, subcategories: current.filter(id => id !== subcategoryId) });
    } else {
      setFormData({ ...formData, subcategories: [...current, subcategoryId] });
    }
  };

  const toggleEquipment = (equipmentId: string) => {
    const current = formData.equipmentIds || [];
    if (current.includes(equipmentId)) {
      setFormData({ ...formData, equipmentIds: current.filter(id => id !== equipmentId) });
    } else {
      setFormData({ ...formData, equipmentIds: [...current, equipmentId] });
    }
  };

  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(categorySearch.toLowerCase()) ||
    (cat.nameArabic && cat.nameArabic.toLowerCase().includes(categorySearch.toLowerCase()))
  );

  const filteredSubcategories = subcategories.filter(sub => 
    sub.name.toLowerCase().includes(subcategorySearch.toLowerCase()) ||
    (sub.nameArabic && sub.nameArabic.toLowerCase().includes(subcategorySearch.toLowerCase()))
  );

  const filteredEquipment = equipment.filter(eq => 
    (eq.name?.en && eq.name.en.toLowerCase().includes(equipmentSearch.toLowerCase())) ||
    (eq.name?.ar && eq.name.ar.toLowerCase().includes(equipmentSearch.toLowerCase()))
  );

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">{t('recipes.loading')}</div>
      </div>
    );
  }

  return (
    <DashboardLayout 
      title={isEditing ? t('recipeForm.editRecipe') : t('recipeForm.createRecipe')}
      subtitle={t('recipeForm.subtitle')}
    >
      <div className="max-w-7xl mx-auto">
        {uploading && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="text-sm text-primary-orange">{uploading}</div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-8">
          {/* Basic Information */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('recipeDetails')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.recipeDetails')} <span className="text-red-500">*</span></h2>
              {expandedSections.recipeDetails ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.recipeDetails && (
            <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipeForm.recipeNameEn')} *
                  <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.titleHintEn')}</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('recipeForm.titlePlaceholderEn')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipeForm.recipeNameAr')}
                  <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.titleHintAr')}</span>
                </label>
                <input
                  type="text"
                  value={formData.titleArabic}
                  onChange={(e) => setFormData({ ...formData, titleArabic: e.target.value })}
                  placeholder={t('recipeForm.titlePlaceholderAr')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('recipeForm.descriptionEn')}
                <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.descriptionHintEn')}</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('recipeForm.descriptionPlaceholderEn')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('recipeForm.descriptionAr')}
                <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.descriptionHintAr')}</span>
              </label>
              <textarea
                value={formData.descriptionArabic}
                onChange={(e) => setFormData({ ...formData, descriptionArabic: e.target.value })}
                placeholder={t('recipeForm.descriptionPlaceholderAr')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipeForm.cuisineTypeEn')} *
                  <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.cuisineHintEn')}</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.cuisine}
                  onChange={(e) => setFormData({ ...formData, cuisine: e.target.value })}
                  placeholder={t('recipeForm.cuisinePlaceholderEn')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipeForm.cuisineTypeAr')}
                  <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.cuisineHintAr')}</span>
                </label>
                <input
                  type="text"
                  value={formData.cuisineArabic}
                  onChange={(e) => setFormData({ ...formData, cuisineArabic: e.target.value })}
                  placeholder={t('recipeForm.cuisinePlaceholderAr')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.timeMinutes')} *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.timeMin}
                  onChange={(e) => setFormData({ ...formData, timeMin: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.difficulty')} *</label>
                <select
                  required
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="Beginner">{t('recipes.difficultyBeginner')}</option>
                  <option value="Intermediate">{t('recipes.difficultyIntermediate')}</option>
                  <option value="Pro">{t('recipes.difficultyPro')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.servings')}</label>
                <input
                  type="number"
                  min="1"
                  value={formData.servings}
                  onChange={(e) => setFormData({ ...formData, servings: parseInt(e.target.value) || 4 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.costEstimate')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.costEstimateEGP}
                  onChange={(e) => setFormData({ ...formData, costEstimateEGP: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">{t('recipeForm.costEstimateHint')}</p>
              </div>
              <div className="mt-4 space-y-3">
                <label className="flex items-center cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.isTrending || false}
                    onChange={(e) => setFormData({ ...formData, isTrending: e.target.checked })}
                    className="checkbox-enhanced w-5 h-5"
                  />
                  <span className="ml-3 text-base font-semibold text-gray-700">{t('recipeForm.trending')}</span>
                </label>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('recipeForm.sourceUrl')}
                <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.sourceUrlHint')}</span>
              </label>
              <input
                type="url"
                value={formData.sourceUrl}
                onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                placeholder={t('recipeForm.sourceUrlPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              />
            </div>

            {/* Categories */}
            <div className="mt-6">
              <div className="relative" ref={categoryDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.categories')}</label>
              
              {/* Selected Categories Display */}
              {formData.categories && formData.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.categories.map((catId) => {
                    const cat = categories.find(c => c._id === catId);
                    if (!cat) return null;
                    return (
                      <span
                        key={catId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-orange text-white"
                      >
                        {cat.name}
                        <button
                          type="button"
                          onClick={() => toggleCategory(catId)}
                          className="ml-2 hover:text-gray-200"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-500">{t('recipeForm.selectCategories')}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showCategoryDropdown ? 'transform rotate-180' : ''}`} />
                </button>
                
                {showCategoryDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2 top-2.5 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t('recipeForm.searchCategories')}
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-48">
                      {categories.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">{t('recipeForm.loadingCategories')}</div>
                      ) : filteredCategories.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">{t('recipeForm.noCategoriesMatch')}</div>
                      ) : (
                        filteredCategories.map((cat) => (
                          <label
                            key={cat._id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.categories?.includes(cat._id) || false}
                              onChange={() => toggleCategory(cat._id)}
                              className="checkbox-enhanced mr-3 flex-shrink-0"
                            />
                            <span>{cat.name} {cat.nameArabic ? `(${cat.nameArabic})` : ''}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* Subcategories */}
            <div className="mt-6">
              <div className="relative" ref={subcategoryDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.subcategories')}</label>
              
                {/* Selected Subcategories Display */}
                {formData.subcategories && formData.subcategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.subcategories.map((subId) => {
                      const sub = subcategories.find(s => s._id === subId);
                      if (!sub) return null;
                      return (
                        <span
                          key={subId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-orange text-white"
                        >
                          {sub.name}
                          <button
                            type="button"
                            onClick={() => toggleSubcategory(subId)}
                            className="ml-2 hover:text-gray-200"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSubcategoryDropdown(!showSubcategoryDropdown)}
                    className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50"
                  >
                    <span className="text-gray-500">{t('recipeForm.selectSubcategories')}</span>
                    <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showSubcategoryDropdown ? 'transform rotate-180' : ''}`} />
                  </button>
                  
                  {showSubcategoryDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-2 top-2.5 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder={t('recipeForm.searchSubcategories')}
                            value={subcategorySearch}
                            onChange={(e) => setSubcategorySearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {subcategories.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500 text-center">{t('recipeForm.loadingSubcategories')}</div>
                        ) : filteredSubcategories.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500 text-center">{t('recipeForm.noSubcategoriesMatch')}</div>
                        ) : (
                          filteredSubcategories.map((sub) => (
                            <label
                              key={sub._id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.subcategories?.includes(sub._id) || false}
                                onChange={() => toggleSubcategory(sub._id)}
                                className="checkbox-enhanced mr-3 flex-shrink-0"
                              />
                              <span>{sub.name} {sub.nameArabic ? `(${sub.nameArabic})` : ''}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>
            )}
          </section>

          {/* Media */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('media')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.media')} <span className="text-red-500">*</span></h2>
              {expandedSections.media ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.media && (
            <div>
            
            {/* Thumbnail — link or upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.thumbnail')}</label>
              {formData.media?.thumbnail && (
                <div className="relative inline-block mb-2">
                  <img src={formData.media.thumbnail} alt="Thumbnail" className="w-32 h-32 object-cover rounded-lg" />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, media: { ...formData.media, thumbnail: '' } })}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="url"
                  value={formData.media?.thumbnail || ''}
                  onChange={(e) => setFormData({ ...formData, media: { ...formData.media, thumbnail: e.target.value.trim() } })}
                  placeholder={t('recipeForm.orEnterImageUrl')}
                  className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2"
                />
                <label className="inline-flex items-center px-4 py-2 bg-primary-orange text-white rounded-lg cursor-pointer hover:bg-orange-600">
                  <PhotoIcon className="w-5 h-5 mr-2" />
                  {t('recipeForm.uploadThumbnail')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'thumbnail')}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Images — multiple, each link or upload, plus to add */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.images')}</label>
              <div className="space-y-3">
                {(formData.media?.images || []).map((img, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 items-center p-3 border border-gray-200 rounded-lg">
                    {img && (
                      <img src={img} alt={`Image ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                    )}
                    <input
                      type="url"
                      value={img || ''}
                      onChange={(e) => {
                        const newImages = [...(formData.media?.images || [])];
                        newImages[idx] = e.target.value.trim();
                        setFormData({ ...formData, media: { ...formData.media, images: newImages } });
                      }}
                      placeholder={t('recipeForm.orEnterImageUrl')}
                      className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <label className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 text-sm">
                      <PhotoIcon className="w-4 h-4 mr-1" />
                      {t('recipeForm.upload')}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const url = await uploadFile(file, 'recipes/images', 'image');
                            const newImages = [...(formData.media?.images || [])];
                            newImages[idx] = url;
                            setFormData({ ...formData, media: { ...formData.media, images: newImages } });
                          } catch (err: any) {
                            alert(err.message);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = formData.media?.images?.filter((_: any, i: number) => i !== idx) || [];
                        setFormData({ ...formData, media: { ...formData.media, images: newImages } });
                      }}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, media: { ...formData.media, images: [...(formData.media?.images || []), ''] } })}
                className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                {t('recipeForm.addImage')}
              </button>
            </div>

            {/* Videos (EN) — multiple, each link or upload, plus to add */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.videosEn')}</label>
              <div className="space-y-3">
                {(formData.media?.videos || []).map((video, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 items-center p-3 border border-gray-200 rounded-lg">
                    <input
                      type="url"
                      value={video || ''}
                      onChange={(e) => {
                        const newVideos = [...(formData.media?.videos || [])];
                        newVideos[idx] = e.target.value.trim();
                        setFormData({ ...formData, media: { ...formData.media, videos: newVideos } });
                      }}
                      placeholder={t('recipeForm.orEnterVideoUrl')}
                      className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <label className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 text-sm">
                      <VideoCameraIcon className="w-4 h-4 mr-1" />
                      {t('recipeForm.upload')}
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const urls = await uploadMultipleFiles([file], 'recipes/videos', 'video');
                            const newVideos = [...(formData.media?.videos || [])];
                            newVideos[idx] = urls[0];
                            setFormData({ ...formData, media: { ...formData.media, videos: newVideos } });
                          } catch (err: any) {
                            alert(err.message);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const newVideos = formData.media?.videos?.filter((_: any, i: number) => i !== idx) || [];
                        setFormData({ ...formData, media: { ...formData.media, videos: newVideos } });
                      }}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, media: { ...formData.media, videos: [...(formData.media?.videos || []), ''] } })}
                className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                {t('recipeForm.addVideo')}
              </button>
            </div>

            {/* Arabic Videos — multiple, each link or upload, plus to add */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.videosAr')}</label>
              <div className="space-y-3">
                {(formData.media?.arVideos || []).map((video, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 items-center p-3 border border-gray-200 rounded-lg">
                    <input
                      type="url"
                      value={video || ''}
                      onChange={(e) => {
                        const newVideos = [...(formData.media?.arVideos || [])];
                        newVideos[idx] = e.target.value.trim();
                        setFormData({ ...formData, media: { ...formData.media, arVideos: newVideos } });
                      }}
                      placeholder={t('recipeForm.orEnterVideoUrl')}
                      className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <label className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 text-sm">
                      <VideoCameraIcon className="w-4 h-4 mr-1" />
                      {t('recipeForm.upload')}
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const urls = await uploadMultipleFiles([file], 'recipes/videos-arabic', 'video');
                            const newVideos = [...(formData.media?.arVideos || [])];
                            newVideos[idx] = urls[0];
                            setFormData({ ...formData, media: { ...formData.media, arVideos: newVideos } });
                          } catch (err: any) {
                            alert(err.message);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const newVideos = formData.media?.arVideos?.filter((_: any, i: number) => i !== idx) || [];
                        setFormData({ ...formData, media: { ...formData.media, arVideos: newVideos } });
                      }}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, media: { ...formData.media, arVideos: [...(formData.media?.arVideos || []), ''] } })}
                className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                {t('recipeForm.addVideo')}
              </button>
            </div>
            </div>
            )}
          </section>

          {/* Ingredients */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('ingredients')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.ingredients')} <span className="text-red-500">*</span></h2>
              {expandedSections.ingredients ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.ingredients && (
            <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">{t('recipeForm.listIngredientsHint')}</p>
              <button
                type="button"
                onClick={addIngredient}
                className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
              >
                {t('recipeForm.addIngredient')}
              </button>
            </div>
            <div className="space-y-4">
              {formData.ingredients.map((ing: any, index: number) => (
                <div
                  key={index}
                  className={`border border-gray-200 rounded-lg p-4 flex gap-3 transition-shadow ${draggingIngredientIndex === index ? 'opacity-60 ring-2 ring-orange-300' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = Number(e.dataTransfer.getData('text/plain'));
                    if (Number.isNaN(from)) return;
                    reorderIngredient(from, index);
                    setDraggingIngredientIndex(null);
                  }}
                >
                  <div className="flex flex-col justify-start pt-8 gap-1 flex-shrink-0">
                    <div
                      role="button"
                      tabIndex={0}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', String(index));
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggingIngredientIndex(index);
                      }}
                      onDragEnd={() => setDraggingIngredientIndex(null)}
                      className="text-gray-400 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
                      title={t('recipeForm.dragToReorder')}
                      aria-label={t('recipeForm.dragToReorder')}
                    >
                      <Bars3Icon className="w-5 h-5" />
                    </div>
                    <button
                      type="button"
                      onClick={() => moveIngredient(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('recipeForm.moveUp')}
                    >
                      <ChevronUpIcon className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveIngredient(index, 'down')}
                      disabled={index === formData.ingredients.length - 1}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('recipeForm.moveDown')}
                    >
                      <ChevronDownIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.name')} *</label>
                        <input
                          type="text"
                          required
                          value={ing.name || ''}
                          onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={t('recipeForm.ingredientNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.nameAr')}</label>
                        <input
                          type="text"
                          value={ing.nameArabic || ''}
                          onChange={(e) => updateIngredient(index, 'nameArabic', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={t('recipeForm.ingredientNamePlaceholder')}
                          dir="rtl"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.amount')}</label>
                        <input
                          type="text"
                          value={ing.amount || ''}
                          onChange={(e) => updateIngredient(index, 'amount', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={t('recipeForm.amountPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.unit')}</label>
                        <select
                          value={ing.unitId || ing.unit || ''}
                          onChange={(e) => updateIngredient(index, 'unitId', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="">{t('recipeForm.selectUnit')}</option>
                          {units.map((unit) => (
                            <option key={unit._id} value={unit._id}>
                              {unit.name?.en || ''} {unit.name?.ar ? `(${unit.name.ar})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.notes')}</label>
                        <input
                          type="text"
                          value={ing.notes || ''}
                          onChange={(e) => updateIngredient(index, 'notes', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={t('recipeForm.additionalNotes')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.notesAr')}</label>
                        <input
                          type="text"
                          value={ing.notesArabic || ''}
                          onChange={(e) => updateIngredient(index, 'notesArabic', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={t('recipeForm.notesPlaceholderAr')}
                          dir="rtl"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={ing.isHeader || false}
                            onChange={(e) => updateIngredient(index, 'isHeader', e.target.checked)}
                            className="checkbox-enhanced mr-2 flex-shrink-0"
                          />
                          <span className="text-sm text-gray-700">{t('recipeForm.header')}</span>
                        </label>
                      </div>
                    </div>
                    <div className={`flex justify-end gap-2 pt-2 border-t border-gray-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {formData.ingredients.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeIngredient(index)}
                          className="inline-flex items-center text-gray-600 hover:text-red-600"
                          title={t('recipeForm.remove')}
                        >
                          <PencilSquareIcon className="w-5 h-5 mr-1" />
                          {t('recipeForm.remove')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
            )}
          </section>

          {/* Steps */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('steps')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.cookingInstructions')} <span className="text-red-500">*</span></h2>
              {expandedSections.steps ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.steps && (
            <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">{t('recipeForm.stepByStepHint')}</p>
              <button
                type="button"
                onClick={addStep}
                className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
              >
                {t('recipeForm.addStep')}
              </button>
            </div>
            <div className="space-y-4">
              {formData.steps.map((step: any, index: number) => (
                <div
                  key={index}
                  className={`border border-gray-200 rounded-lg p-4 flex gap-3 transition-shadow ${draggingStepIndex === index ? 'opacity-60 ring-2 ring-orange-300' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = Number(e.dataTransfer.getData('text/plain'));
                    if (Number.isNaN(from)) return;
                    reorderStep(from, index);
                    setDraggingStepIndex(null);
                  }}
                >
                  <div className="flex flex-col justify-start pt-2 gap-1 flex-shrink-0">
                    <div
                      role="button"
                      tabIndex={0}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', String(index));
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggingStepIndex(index);
                      }}
                      onDragEnd={() => setDraggingStepIndex(null)}
                      className="text-gray-400 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
                      title={t('recipeForm.dragToReorder')}
                      aria-label={t('recipeForm.dragToReorder')}
                    >
                      <Bars3Icon className="w-5 h-5" />
                    </div>
                    <button
                      type="button"
                      onClick={() => moveStep(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('recipeForm.moveUp')}
                    >
                      <ChevronUpIcon className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(index, 'down')}
                      disabled={index === formData.steps.length - 1}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('recipeForm.moveDown')}
                    >
                      <ChevronDownIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-4">{t('recipeForm.step')} {step.stepNumber}</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.stepTitleEn')}</label>
                        <p className="text-xs text-gray-500 mb-1">{t('recipeForm.stepTitleHint')}</p>
                        <input
                          type="text"
                          value={step.title || ''}
                          onChange={(e) => updateStep(index, 'title', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={t('recipeForm.stepTitleEn')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.stepTitleAr')}</label>
                        <input
                          type="text"
                          value={step.titleArabic || ''}
                          onChange={(e) => updateStep(index, 'titleArabic', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          dir="rtl"
                          placeholder={t('recipeForm.stepTitleAr')}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.instruction')} *</label>
                      <textarea
                        required
                        value={step.instruction}
                        onChange={(e) => updateStep(index, 'instruction', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.instructionAr')}</label>
                      <textarea
                        value={step.instructionArabic}
                        onChange={(e) => updateStep(index, 'instructionArabic', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('recipeForm.stepDuration')}
                          <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.stepDurationHint')}</span>
                        </label>
                        <input
                          type="number"
                          value={step.timeMinutes || 0}
                          onChange={(e) => updateStep(index, 'timeMinutes', parseInt(e.target.value) || 0)}
                          placeholder={t('recipeForm.stepPlaceholder')}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('recipeForm.stepImage')}
                          <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.stepImageHint')}</span>
                        </label>
                        {step.image && (
                          <div className="relative mb-2">
                            <img src={step.image} alt={`Step ${step.stepNumber}`} className="w-full h-32 object-cover rounded-lg" />
                            <button
                              type="button"
                              onClick={() => updateStep(index, 'image', '')}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={step.image || ''}
                            onChange={(e) => updateStep(index, 'image', e.target.value)}
                            placeholder={t('recipeForm.orEnterImageUrlShort')}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                          <label className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200">
                            <PhotoIcon className="w-4 h-4 mr-1" />
                            {t('recipeForm.upload')}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleStepImageUpload(index, e.target.files[0]);
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('recipeForm.stepVideo')}
                          <span className="text-xs text-gray-500 font-normal ml-2">{t('recipeForm.stepVideoHint')}</span>
                        </label>
                        {step.video && (
                          <div className="mb-2">
                            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm truncate">{step.video}</span>
                              <button
                                type="button"
                                onClick={() => updateStep(index, 'video', '')}
                                className="text-red-600 hover:text-red-800"
                              >
                                <XMarkIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={step.video || ''}
                            onChange={(e) => updateStep(index, 'video', e.target.value)}
                            placeholder={t('recipeForm.orEnterVideoUrlShort')}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                          <label className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200">
                            <VideoCameraIcon className="w-4 h-4 mr-1" />
                            {t('recipeForm.upload')}
                            <input
                              type="file"
                              accept="video/*"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleStepVideoUpload(index, e.target.files[0]);
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {formData.steps.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStep(index)}
                        className="inline-flex items-center text-gray-600 hover:text-red-600"
                        title={t('recipeForm.remove')}
                      >
                        <PencilSquareIcon className="w-5 h-5 mr-1" />
                        {t('recipeForm.remove')}
                      </button>
                    )}
                  </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
            )}
          </section>

          {/* Equipments */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('equipment')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.equipments')} <span className="text-red-500">*</span></h2>
              {expandedSections.equipment ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.equipment && (
            <div>
            <div className="relative" ref={equipmentDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.selectEquipments')}</label>
              
              {/* Selected Equipments Display */}
              {formData.equipmentIds && formData.equipmentIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.equipmentIds.map((equipId: string) => {
                    const equip = equipment.find(e => e._id === equipId);
                    if (!equip) return null;
                    return (
                      <span
                        key={equipId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-primary-orange text-white"
                      >
                        {equip.iconUrl ? (
                          <img src={equip.iconUrl} alt="" className="w-5 h-5 object-contain rounded flex-shrink-0" />
                        ) : null}
                        {equip.name?.en || ''} {equip.name?.ar ? `(${equip.name.ar})` : ''}
                        <button
                          type="button"
                          onClick={() => toggleEquipment(equipId)}
                          className="ml-2 hover:text-gray-200"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEquipmentDropdown(!showEquipmentDropdown)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-500">{t('recipeForm.selectEquipments')}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showEquipmentDropdown ? 'transform rotate-180' : ''}`} />
                </button>
                
                {showEquipmentDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2 top-2.5 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t('recipeForm.searchEquipments')}
                          value={equipmentSearch}
                          onChange={(e) => setEquipmentSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-80 min-h-[120px]">
                      {equipment.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">{t('recipeForm.loadingEquipments')}</div>
                      ) : filteredEquipment.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">{t('recipeForm.noEquipmentsMatch')}</div>
                      ) : (
                        filteredEquipment.map((equip) => (
                          <label
                            key={equip._id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.equipmentIds?.includes(equip._id) || false}
                              onChange={() => toggleEquipment(equip._id)}
                              className="checkbox-enhanced flex-shrink-0"
                            />
                            {equip.iconUrl ? (
                              <img src={equip.iconUrl} alt="" className="w-6 h-6 object-contain rounded border border-gray-200 bg-gray-50 flex-shrink-0" />
                            ) : null}
                            <span>{equip.name?.en || ''} {equip.name?.ar ? `(${equip.name.ar})` : ''}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
            )}
          </section>

          {/* Nutrition */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('nutrition')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.nutrition')} <span className="text-red-500">*</span></h2>
              {expandedSections.nutrition ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.nutrition && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.calories')}</label>
                <input
                  type="number"
                  value={formData.nutrition?.calories || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    nutrition: { ...formData.nutrition, calories: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.protein')}</label>
                <input
                  type="number"
                  value={formData.nutrition?.protein || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    nutrition: { ...formData.nutrition, protein: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.carbs')}</label>
                <input
                  type="number"
                  value={formData.nutrition?.carbs || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    nutrition: { ...formData.nutrition, carbs: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.fats')}</label>
                <input
                  type="number"
                  value={formData.nutrition?.fats || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    nutrition: { ...formData.nutrition, fats: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.fiber')}</label>
                <input
                  type="number"
                  value={formData.nutrition?.fiber || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    nutrition: { ...formData.nutrition, fiber: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.sugar')}</label>
                <input
                  type="number"
                  value={formData.nutrition?.sugar || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    nutrition: { ...formData.nutrition, sugar: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            </div>
            )}
          </section>

          {/* Tags */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('tags')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.tags')}</h2>
              {expandedSections.tags ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.tags && (
            <div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.tags')}</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeArrayItem('tags', idx)}
                        className="ml-2 text-gray-600 hover:text-red-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    ref={tagInputRef}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = tagInputRef.current;
                        if (input && input.value.trim()) {
                          addArrayItem('tags', input.value.trim());
                          input.value = '';
                        }
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('recipeForm.enterTagPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = tagInputRef.current;
                      if (input && input.value.trim()) {
                        addArrayItem('tags', input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.tagsAr')}</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tagsArabic?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                      dir="rtl"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeArrayItem('tagsArabic', idx)}
                        className="mr-2 text-gray-600 hover:text-red-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const target = e.target as HTMLInputElement;
                        if (target.value.trim()) {
                          addArrayItem('tagsArabic', target.value.trim());
                          target.value = '';
                        }
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('recipeForm.tagsArPlaceholder')}
                    dir="rtl"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                      if (input && input.value.trim()) {
                        addArrayItem('tagsArabic', input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            </div>
            )}
          </section>

          {/* Dietary Tags */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('dietaryTags')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.dietaryTags')}</h2>
              {expandedSections.dietaryTags ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.dietaryTags && (
            <div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.dietaryTags')}</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.dietaryTags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeArrayItem('dietaryTags', idx)}
                        className="ml-2 text-gray-600 hover:text-red-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    ref={dietaryTagInputRef}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = dietaryTagInputRef.current;
                        if (input && input.value.trim()) {
                          addArrayItem('dietaryTags', input.value.trim());
                          input.value = '';
                        }
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('recipeForm.enterDietaryTag')}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = dietaryTagInputRef.current;
                      if (input && input.value.trim()) {
                        addArrayItem('dietaryTags', input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dietary Tags (Arabic)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.dietaryTagsArabic?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                      dir="rtl"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeArrayItem('dietaryTagsArabic', idx)}
                        className="mr-2 text-gray-600 hover:text-red-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const target = e.target as HTMLInputElement;
                        if (target.value.trim()) {
                          addArrayItem('dietaryTagsArabic', target.value.trim());
                          target.value = '';
                        }
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('recipeForm.enterDietaryTag')}
                    dir="rtl"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                      if (input && input.value.trim()) {
                        addArrayItem('dietaryTagsArabic', input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dietary Tags (Arabic)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.dietaryTagsArabic?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                      dir="rtl"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeArrayItem('dietaryTagsArabic', idx)}
                        className="mr-2 text-gray-600 hover:text-red-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const target = e.target as HTMLInputElement;
                        if (target.value.trim()) {
                          addArrayItem('dietaryTagsArabic', target.value.trim());
                          target.value = '';
                        }
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('recipeForm.enterDietaryTag')}
                    dir="rtl"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                      if (input && input.value.trim()) {
                        addArrayItem('dietaryTagsArabic', input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            </div>
            )}
          </section>

          {/* Seasonal Tags */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('seasonalTags')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.seasonalTags')}</h2>
              {expandedSections.seasonalTags ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.seasonalTags && (
            <div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.seasonalTags')}</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.seasonalTags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeArrayItem('seasonalTags', idx)}
                        className="ml-2 text-gray-600 hover:text-red-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    ref={seasonalTagInputRef}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = seasonalTagInputRef.current;
                        if (input && input.value.trim()) {
                          addArrayItem('seasonalTags', input.value.trim());
                          input.value = '';
                        }
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('recipeForm.enterSeasonalTag')}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = seasonalTagInputRef.current;
                      if (input && input.value.trim()) {
                        addArrayItem('seasonalTags', input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            </div>
            )}
          </section>

          {/* Pro Tips */}
          <section className="border border-gray-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => toggleSection('proTips')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.proTips')}</h2>
              {expandedSections.proTips ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.proTips && (
            <div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.proTips')}</label>
                <div className="space-y-2">
                  {formData.proTips?.map((tip, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={tip}
                        onChange={(e) => {
                          const newTips = [...(formData.proTips || [])];
                          newTips[idx] = e.target.value;
                          setFormData({ ...formData, proTips: newTips });
                        }}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                        placeholder={t('recipeForm.proTipPlaceholder')}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('proTips', idx)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('proTips', '')}
                    className="flex items-center space-x-2 px-4 py-2 text-primary-orange hover:text-orange-600 border border-primary-orange rounded-lg hover:bg-orange-50"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('recipeForm.addProTip')}</span>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('recipeForm.proTipsAr')}</label>
                <div className="space-y-2">
                  {formData.proTipsArabic?.map((tip, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={tip}
                        onChange={(e) => {
                          const newTips = [...(formData.proTipsArabic || [])];
                          newTips[idx] = e.target.value;
                          setFormData({ ...formData, proTipsArabic: newTips });
                        }}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                        placeholder={t('recipeForm.proTipPlaceholderAr')}
                        dir="rtl"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('proTipsArabic', idx)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('proTipsArabic', '')}
                    className="flex items-center space-x-2 px-4 py-2 text-primary-orange hover:text-orange-600 border border-primary-orange rounded-lg hover:bg-orange-50"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('recipeForm.addProTipAr')}</span>
                  </button>
                </div>
              </div>
            </div>
            </div>
            )}
          </section>

          {/* Buying options: collapsible, last section before actions */}
          <section className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
            <button
              type="button"
              onClick={() => toggleSection('buyingOptions')}
              className="w-full flex justify-between items-center mb-4"
            >
              <h2 className="text-xl font-bold text-gray-900">{t('recipeForm.buyingOptions') || 'Buying options'}</h2>
              {expandedSections.buyingOptions ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.buyingOptions && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.mealBoxPrice')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.mealBoxPriceEGP ?? 0}
                      onChange={(e) => setFormData({ ...formData, mealBoxPriceEGP: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.preparedMealPrice')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.preparedMealPriceEGP ?? 0}
                      onChange={(e) => setFormData({ ...formData, preparedMealPriceEGP: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.inventoryMealBox') ?? 'Meal box inventory'}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.inventoryQuantityMealBox ?? 0}
                      onChange={(e) => setFormData({ ...formData, inventoryQuantityMealBox: parseInt(e.target.value, 10) || 0 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipeForm.inventoryPreparedMeal') ?? 'Prepared meal inventory'}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.inventoryQuantityPreparedMeal ?? 0}
                      onChange={(e) => setFormData({ ...formData, inventoryQuantityPreparedMeal: parseInt(e.target.value, 10) || 0 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center cursor-pointer p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
                    <input
                      ref={buyBoxAllowedInputRef}
                      data-testid="recipe-form-buy-box-allowed"
                      type="checkbox"
                      checked={formData.buyBoxAllowed || false}
                      onChange={(e) => setFormData((prev) => ({ ...prev, buyBoxAllowed: e.target.checked }))}
                      className="checkbox-enhanced w-5 h-5"
                    />
                    <span className="ml-3 text-base font-semibold text-gray-700">{t('recipeForm.buyBoxAllowed')}</span>
                  </label>
                  <label className="flex items-center cursor-pointer p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
                    <input
                      ref={buyPreparedMealAllowedInputRef}
                      data-testid="recipe-form-buy-prepared-meal-allowed"
                      type="checkbox"
                      checked={formData.buyPreparedMealAllowed || false}
                      onChange={(e) => setFormData((prev) => ({ ...prev, buyPreparedMealAllowed: e.target.checked }))}
                      className="checkbox-enhanced w-5 h-5"
                    />
                    <span className="ml-3 text-base font-semibold text-gray-700">{t('recipeForm.buyPreparedMealAllowed')}</span>
                  </label>
                </div>
              </div>
            )}
          </section>

          {/* Form Actions */}
          <div className="flex flex-wrap justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/?tab=recipes')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('recipeForm.cancel')}
            </button>
            {isEditing ? (
              <button
                type="button"
                data-testid="recipe-form-submit"
                onClick={(e) => handleSubmit(e, formData.isPublished)}
                disabled={loading}
                className="px-6 py-2 bg-primary-orange hover:bg-orange-600 text-white rounded-lg disabled:opacity-50"
              >
                {loading ? t('recipeForm.saving') : t('recipeForm.updateRecipe')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={loading}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {loading ? t('recipeForm.saving') : t('recipeForm.createAsDraft')}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading}
                  className="px-6 py-2 bg-primary-orange hover:bg-orange-600 text-white rounded-lg disabled:opacity-50"
                >
                  {loading ? t('recipeForm.publishing') : t('recipeForm.createAndPublish')}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

